/**
 * GROWTH-R1 — client helpers for recovery sync / active card (no marketing send).
 */
import { getCart, writeCart, type CartV2Item } from "./cart-v2.js";
import { waitForTelegramInitData } from "../lib/telegram.js";

function apiBase(): string {
  return "";
}

async function authHeaders(): Promise<HeadersInit> {
  const initData = await waitForTelegramInitData(2000);
  const h: Record<string, string> = { Accept: "application/json", "Content-Type": "application/json" };
  if (initData) h.Authorization = `tma ${initData}`;
  return h;
}

export async function syncCartRecoveryToServer(input?: {
  checkoutStep?: string | null;
}): Promise<Record<string, unknown> | null> {
  try {
    const items = getCart();
    const headers = await authHeaders();
    const res = await fetch(`${apiBase()}/api/miniapp/recovery/cart/sync`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        items: items.map((i) => ({
          product_id: i.id,
          title: i.title,
          qty: i.qty,
          unit_price_uah: i.priceUah,
          image_url: i.imageUrl,
          availability: i.stockStatus === "out_of_stock" ? "unavailable" : "available",
        })),
        updated_at: new Date().toISOString(),
        checkout_step: input?.checkoutStep ?? null,
      }),
    });
    if (!res.ok) return null;
    const json = (await res.json()) as Record<string, unknown>;
    if (json.superseded_client === true && Array.isArray(json.items)) {
      const next: CartV2Item[] = (json.items as Array<Record<string, unknown>>).map((i) => ({
        id: String(i.product_id ?? ""),
        title: String(i.title ?? ""),
        qty: Number(i.qty ?? 0),
        priceUah: Number(i.unit_price_uah ?? 0),
        imageUrl: i.image_url == null ? null : String(i.image_url),
        availabilityLabel: i.availability === "unavailable" ? "unavailable" : null,
      }));
      writeCart(next.filter((x) => x.id && x.qty > 0));
    }
    return json;
  } catch {
    return null;
  }
}

export async function markCheckoutRecoveryProgress(step: string): Promise<void> {
  try {
    const headers = await authHeaders();
    await fetch(`${apiBase()}/api/miniapp/recovery/checkout/progress`, {
      method: "POST",
      headers,
      body: JSON.stringify({ step }),
    });
  } catch {
    /* non-blocking */
  }
}

export async function fetchActiveRecovery(): Promise<Record<string, unknown> | null> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${apiBase()}/api/miniapp/recovery/active`, { method: "GET", headers });
    if (!res.ok) return null;
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function revalidateCartAgainstCatalog(
  items: CartV2Item[],
  catalog: Array<{ id: string; priceUah: number | null; stockStatus: string; isSellable: boolean }>,
): { items: CartV2Item[]; priceChanged: boolean; unavailableTitles: string[] } {
  const byId = new Map(catalog.map((p) => [p.id, p]));
  let priceChanged = false;
  const unavailableTitles: string[] = [];
  const next = items.map((item) => {
    const p = byId.get(item.id);
    if (!p) {
      unavailableTitles.push(item.title);
      return { ...item, stockStatus: "out_of_stock", availabilityLabel: "unavailable" };
    }
    const raw = String(p.stockStatus ?? "").toLowerCase();
    const unavailable =
      !p.isSellable ||
      ["out_of_stock", "unavailable", "not_available", "sold_out", "absent", "discontinued"].includes(raw);
    if (unavailable) {
      unavailableTitles.push(item.title);
      return { ...item, stockStatus: p.stockStatus || "out_of_stock", availabilityLabel: "unavailable" };
    }
    const price = Number(p.priceUah);
    if (Number.isFinite(price) && price > 0 && price !== item.priceUah) {
      priceChanged = true;
      return { ...item, priceUah: price, stockStatus: p.stockStatus, availabilityLabel: null };
    }
    return { ...item, stockStatus: p.stockStatus };
  });
  return { items: next, priceChanged, unavailableTitles };
}

